/**
 * What the terminal wall prints.
 *
 * Three tiles, and **only one of them is writing**. Three panes producing output
 * at once would be selling a thing the product does not do: a route places a
 * unit of work on one account, on one host, and the other tiles an operator has
 * open are a session waiting for its turn and a session that has finished.
 *
 * The two idle states are the real mechanisms, not decoration:
 *   · `@kimene_waiting` and `@kimene_done` are tmux options the agent sets, read
 *     back by `internal/terminal/hooks.go`. The console knows a session is
 *     waiting because the session said so, not because output went quiet.
 * Each line carries a tone so the transcript is readable without colour being
 * the only signal — the words say `bekliyor` and `tamamlandı` too.
 */

export type LineTone = 'dim' | 'ok' | 'warn' | 'err' | 'acc' | 'info' | undefined

export interface Line {
  text: string
  tone?: LineTone
}

export interface Tile {
  /** The tmux session name, printed as the machine value it is. */
  title: string
  state: 'running' | 'waiting' | 'done'
  lines: readonly Line[]
}

export const TILES: readonly Tile[] = [
  {
    title: 'kimene-01JQ8F3K2M',
    state: 'running',
    lines: [
      { text: '$ claude --model opus-5', tone: 'dim' },
      { text: '· atlas-web/src/api/routes.ts okunuyor', tone: 'dim' },
      { text: '· 3 test yazıldı, 2’si kırmızı', tone: 'dim' },
      { text: '→ pnpm test src/api', tone: 'acc' },
      { text: '  ✓ 41 passed  ✗ 2 failed', tone: 'warn' },
      { text: '· hatanın yeri bulundu: routes.ts:118', tone: 'dim' },
    ],
  },
  {
    title: 'kimene-01JQ8F3K7P',
    state: 'waiting',
    lines: [
      { text: '$ codex exec --sandbox workspace-write', tone: 'dim' },
      { text: '· mercury-api/migrations gözden geçirildi', tone: 'dim' },
      { text: '', tone: 'dim' },
      { text: '@kimene_waiting  onay bekliyor', tone: 'info' },
      { text: 'şema değişikliği için insan imzası gerekiyor', tone: 'dim' },
    ],
  },
  {
    title: 'kimene-01JQ8F2X40',
    state: 'done',
    lines: [
      { text: '$ claude --model sonnet-5', tone: 'dim' },
      { text: '· kepler-veri/etl/load.py düzeltildi', tone: 'dim' },
      { text: '→ pytest -q', tone: 'dim' },
      { text: '  118 passed in 6.42s', tone: 'ok' },
      { text: '@kimene_done  tamamlandı', tone: 'ok' },
    ],
  },
] as const

/**
 * The evidence a task must carry before `COMPLETED` is reachable.
 *
 * The fourth is deliberately absent. `internal/domain/task_state.go` allows
 * `COMPLETED` only from `REVIEWING` or `APPROVAL_REQUIRED`, and the API has no
 * path that writes a task state directly — so a task in this shape is a task
 * that cannot be finished, and the figure shows the gate closed rather than a
 * tidy row of four green ticks.
 */
export interface Artifact {
  kind: 'diff' | 'test' | 'log' | 'review'
  ref: string
  present: boolean
}

export const EVIDENCE: readonly Artifact[] = [
  { kind: 'diff', ref: 'sha256:0f0f7c21', present: true },
  { kind: 'test', ref: '118 passed · 0 failed', present: true },
  { kind: 'log', ref: 'sha256:0f0fa93e', present: true },
  { kind: 'review', ref: '—', present: false },
] as const

/**
 * The fencing timeline.
 *
 * ADR-0003 claims something weaker and truer than a distributed lock: a holder
 * that comes back late cannot apply an effect. Token 17 is stale by the time it
 * writes, and the store refuses it — `17 < 18`. The refusal is the content.
 */
export interface FenceEvent {
  /** 0–1 along the timeline. */
  at: number
  token: number
  actor: string
  outcome: 'granted' | 'renewed' | 'expired' | 'refused'
}

export const FENCE: readonly FenceEvent[] = [
  { at: 0.04, token: 17, actor: 'srv-ist-1', outcome: 'granted' },
  { at: 0.24, token: 17, actor: 'srv-ist-1', outcome: 'renewed' },
  { at: 0.46, token: 17, actor: 'srv-ist-1', outcome: 'expired' },
  { at: 0.6, token: 18, actor: 'srv-fra-1', outcome: 'granted' },
  { at: 0.82, token: 17, actor: 'srv-ist-1', outcome: 'refused' },
] as const

/** Where the timeline's playhead rests: the refusal. */
export const FENCE_ARGUMENT_AT = 0.82
